'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  principal: 10000,
  rate: 5,
  time: 5,
  compound: 'annually' as const,
};

const PRESETS = {
  conservative: {
    principal: 5000,
    rate: 3,
    time: 10,
    compound: 'annually' as const,
  },
  moderate: {
    principal: 10000,
    rate: 5,
    time: 5,
    compound: 'monthly' as const,
  },
  aggressive: {
    principal: 20000,
    rate: 8,
    time: 3,
    compound: 'monthly' as const,
  },
};

export default function CalculatorInterest() {
  const [principal, setPrincipal] = useState(INITIAL_STATE.principal);
  const [rate, setRate] = useState(INITIAL_STATE.rate);
  const [time, setTime] = useState(INITIAL_STATE.time);
  const [compound, setCompound] = useState(INITIAL_STATE.compound);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (setter: (v: any) => void, value: any) => {
    setter(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const handleReset = () => {
    setPrincipal(INITIAL_STATE.principal);
    setRate(INITIAL_STATE.rate);
    setTime(INITIAL_STATE.time);
    setCompound(INITIAL_STATE.compound);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setPrincipal(values.principal);
    setRate(values.rate);
    setTime(values.time);
    setCompound(values.compound);
  };

  const getCompoundFrequency = () => {
    switch (compound) {
      case 'annually': return 1;
      case 'monthly': return 12;
      case 'quarterly': return 4;
      case 'daily': return 365;
      default: return 1;
    }
  };

  const compoundFrequency = getCompoundFrequency();
  const simpleInterest = principal * (rate / 100) * time;
  const simpleTotal = principal + simpleInterest;
  
  const compoundTotal = principal * Math.pow(1 + rate / 100 / compoundFrequency, compoundFrequency * time);
  const compoundInterest = compoundTotal - principal;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Principal Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={principal}
                onChange={(e) => handleChange(setPrincipal, Number(e.target.value))}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Interest Rate</label>
            <div className="relative">
              <input
                type="number"
                value={rate}
                onChange={(e) => handleChange(setRate, Number(e.target.value))}
                step={0.1}
                className="w-full pl-3 pr-10 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Time Period (years)</label>
            <input
              type="number"
              value={time}
              onChange={(e) => handleChange(setTime, Number(e.target.value))}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Compound Frequency</label>
            <select
              value={compound}
              onChange={(e) => handleChange(setCompound, Number(e.target.value))}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            >
              <option value={1}>Annually</option>
              <option value={2}>Semi-annually</option>
              <option value={4}>Quarterly</option>
              <option value={12}>Monthly</option>
              <option value={365}>Daily</option>
            </select>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-teal-500 via-cyan-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-3">Compound Interest</div>
            <div className="text-4xl font-bold mb-1">${compoundTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            <div className="text-sm opacity-90">Interest earned: ${compoundInterest.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          </div>

          <div className="bg-slate-50 rounded-lg p-6">
            <div className="text-sm font-semibold text-slate-700 mb-2">Simple Interest</div>
            <div className="text-3xl font-bold text-slate-900 mb-1">${simpleTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            <div className="text-sm text-slate-600">Interest earned: ${simpleInterest.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          </div>

          <div className="bg-emerald-50 rounded-lg p-4 border-2 border-emerald-200">
            <div className="text-sm text-emerald-800">
              <strong>Compound vs Simple:</strong> You earn ${(compoundInterest - simpleInterest).toLocaleString('en-US', { maximumFractionDigits: 0 })} more with compound interest
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}